import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild, WritableSignal, computed, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { CUSTOM_DATE_FORMATS } from '../../../shared/constants/date-formtats';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { PaymentMethodService } from './payment-method.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { AddressService } from '../../../shared/address.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../../shared/notification/notification.service';
import { AccountService } from '../../../shared/account.service';
import { MatSelectModule } from '@angular/material/select';
import { ArrayOptionsItem, PaymentDataPayload } from '../../../shared/types/account.types';
import { tap } from 'rxjs';
import { CollectJSFieldTypes, CollectJSValidationResult, initialCollectJSValidationResult } from './payment-method.types';
import { LoadingService } from '../../../shared/loading.service';
import { skip } from 'rxjs';
import { PendingMembershipService } from '../../../shared/pending-membership/pending-membership.service';
import {
  MEMBERSHIP_CONFIRMATION_ERROR_CODES,
  MembershipConfirmation,
  PendingMembershipCharge
} from '../../../shared/pending-membership/pending-membership.types';

@Component({
    selector: 'app-payment-method',
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS.MONTH_AND_YEAR },
        {
            provide: Loader,
            useValue: new Loader({
                apiKey: environment.apis.google.places,
                libraries: ['places'],
                region: 'US',
            })
        }
    ],
    imports: [
        MatFormFieldModule,
        MatDatepickerModule,
        MatInputModule,
        ReactiveFormsModule,
        CommonModule,
        MatSlideToggleModule,
        MatCheckboxModule,
        FormsModule,
        MatProgressBarModule,
        MatSelectModule
    ],
    templateUrl: './payment-method.component.html',
    styleUrl: './payment-method.component.scss'
})
export class PaymentMethodComponent implements OnInit {

  @ViewChild('autocompleteInput') autocompleteInput!: ElementRef<HTMLInputElement>;

  private formBuilder = inject(FormBuilder);
  private paymentMethodService = inject(PaymentMethodService);
  private addressService = inject(AddressService);
  private notificationService = inject(NotificationService);
  private accountService = inject(AccountService);
  private loadingService = inject(LoadingService);
  private activeModal = inject(NgbActiveModal, { optional: true });
  #pendingMembershipService = inject(PendingMembershipService);

  @Input() inModal = false;

  private paymentMethodInfo = toObservable(this.paymentMethodService.paymentMethod);

  googleAddress: any = signal(undefined);
  hasDeliveryAddressCoverage: any = signal(false);
  isGoogleAddressFilled = signal(false);
  isLoadingContent: any = computed(() => this.paymentMethodService.isLoadingContent());

  isEditionModeEnable = signal(false);

  toggleBillingForm = signal(false);

  creditCardForm!: FormGroup;
  billingForm!: FormGroup;

  states: WritableSignal<ArrayOptionsItem[]> = signal([]);

  //#region NMI - CollectJS config:
  #cdr = inject(ChangeDetectorRef);

  // TODO: This is to force interaction with the app for NMI issue. It's necessary to look for a best way to handle this:
  @ViewChild('btnNMIChangesTrigger') btnNMIChangesTrigger!: ElementRef;

  collectJS_ccnumber_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)
  collectJS_ccexp_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)
  collectJS_cvv_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)

  collectJSResponse: WritableSignal<any> = signal(null);
  //#endregion

  isDisabledSaveChanges: WritableSignal<boolean> = signal(false);

  //#region Pending membership confirmation (DEV-11022):

  /** The membership saving a card is about to start, while it is still unconfirmed.
   *  Non-null is what swaps the save button for the one that opens the modal. */
  pendingMembershipCharge: WritableSignal<PendingMembershipCharge | null> = signal(null);

  /** The option the customer picked, sent with the card so the backend charges it. */
  #membershipConfirmation: MembershipConfirmation | null = null;

  /** The button CollectJS tokenizes from. Kept in the DOM but hidden while the
   *  confirmation is pending, and clicked once the customer has chosen. */
  @ViewChild('nmiPayButtonRef') nmiPayButtonRef!: ElementRef<HTMLButtonElement>;

  //#endregion

  constructor() {
    this.initForms();
  }

  ngOnInit(): void {
    this.getPaymentMethodInfo();
    this.#checkPendingMembershipCharge();
    this.#subscribeToCollectJsValidationChanges();
    this.#subscribeToCollectJSResponse();
    this.#subscribeToCollectJSTimeout();
  }

  private initForms() {
    this.creditCardForm = this.formBuilder.group({
      creditCardNumber: [{ value: '', disabled: true }],
      firstName: [{ value: '', disabled: true }, [Validators.required]],
      lastName: [{ value: '', disabled: true }, [Validators.required]],
    })

    this.billingForm = this.formBuilder.group({
      street: [''],
      additional: [''],
      city: [''],
      state: [''],
      zipCode: ['']
    })
  }

  private getPaymentMethodInfo() {
    this.paymentMethodService.getPaymenthMethodInfo();
    this.paymentMethodInfo
      .pipe(
        skip(1),
      )
      .subscribe((data: any) => {

        if (!data) {
          this.enableEditionMode();
          return;
        }

        const creditCardControl = this.creditCardForm.get('creditCardNumber');
        if (creditCardControl && data.number != null) {
          creditCardControl.setValue(data.number.toString().padStart(16, '*'));
          this.formatCreditCardNumber();
        }
        const firstNameControl = this.creditCardForm.get('firstName');
        if (firstNameControl) {
          firstNameControl.disable();
          firstNameControl.setValue(data.firstName);
        }
        const lastNameControl = this.creditCardForm.get('lastName');
        if (lastNameControl) {
          lastNameControl.disable();
          lastNameControl.setValue(data.lastName);
        }

        if (this.inModal) {
          this.enableEditionMode();
        }
      })
  }

  discardChangesHandler() {
    this.isEditionModeEnable.set(false);
    this.toggleBillingForm.set(false);
    const creditCardNumberControl = this.creditCardForm.get('creditCardNumber');
    const firstName = this.creditCardForm.get('firstName');
    const lastName = this.creditCardForm.get('lastName');
    creditCardNumberControl?.disable();
    firstName?.disable();
    lastName?.disable();
    this.billingForm.reset();
    this.creditCardForm.reset();
    this.getPaymentMethodInfo();
  }

  formatCreditCardNumber() {
    const control = this.creditCardForm.get('creditCardNumber');
    if (!control) return;
    let value = control.value;
    let cleaned = !this.isEditionModeEnable() ? value.replace(/[^\d*]/g, '') : value.replace(/\D/g, '');
    cleaned = cleaned.substring(0, 16);
    let formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    control.setValue(formatted);
  }

  enableEditionMode() {
    this.isEditionModeEnable.set(true);
    const creditCardNumberControl = this.creditCardForm.get('creditCardNumber');
    const firstNameControl = this.creditCardForm.get('firstName');
    const lastNameControl = this.creditCardForm.get('lastName');
    creditCardNumberControl?.enable();
    creditCardNumberControl?.setValue('');
    firstNameControl?.enable();
    firstNameControl?.setValue('');
    lastNameControl?.enable();
    lastNameControl?.setValue('');
    setTimeout(() => {
      this.paymentMethodService.setUpNMI();
    }, 1);
  }

  validateErrors(controlName: string): string | null {
    const control = this.creditCardForm.get(controlName);
    if (control?.valid || (!control?.dirty && !control?.touched)) return null;
    if (control?.hasError('required')) {
      return `This field is required`
    } else if (control?.hasError('email')) {
      return 'Invalid email format'
    }
    return null;
  }

  handleSaveChangesClicked() {
    if (!this.#isFormValid()) return;

    this.updatePaymentMethod();
  }

  /** Reports the mandatory fields, so a half-filled form is caught before anything
   *  is tokenized or the customer is asked to agree to a charge. */
  #isFormValid(): boolean {
    if (this.creditCardForm.valid && this.billingForm.valid) return true;

    this.creditCardForm.markAllAsTouched();
    this.notificationService.show({ text: 'Validate mandatory fields', type: 'error' });
    return false;
  }

  private updatePaymentMethod() {
    const payload = this.setUpPaymentMethodPayload();
    this.accountService.addPaymentMethod(payload).subscribe({
      next: (res: any): any => {
        this.loadingService.close();
        this.isDisabledSaveChanges.set(false);

        // The backend refused to charge a membership the customer has not agreed
        // to. It answers with the current amounts, so the modal can be reopened
        // from them and the save retried (DEV-11022).
        if (res?.error && MEMBERSHIP_CONFIRMATION_ERROR_CODES.includes(res.code)) {
          this.#promptMembershipConfirmation(res.data);
          return;
        }

        if (!res.error) {
          if (res.inProcess) {
            this.notificationService.show({ text: 'Your payment update is in progress and this can take a few minutes', type: 'success' });
          }
          else {
            this.notificationService.show({ text: res.message, type: 'success' });
            if (this.inModal && this.activeModal) {
              this.activeModal.close({ saved: true });
              return;
            }
            this.discardChangesHandler();
          }
        }

        this.btnNMIChangesTrigger?.nativeElement?.click();
      }
    });
  }

  private setUpPaymentMethodPayload(): PaymentDataPayload {
    const billingFormValues = this.billingForm.getRawValue();
    const creditCardFormValues = this.creditCardForm.getRawValue();
    return {
      billingAddress: {
        additional: billingFormValues.additional || undefined,
        city: billingFormValues.city || undefined,
        stateCode: billingFormValues.state || undefined,
        street: billingFormValues.street || undefined,
        zip: billingFormValues.zipCode || undefined
      },
      card: {
        firstName: creditCardFormValues.firstName,
        lastName: creditCardFormValues.lastName,
        number: this.collectJSResponse().card,
        token: this.collectJSResponse().token,
        expiration: this.collectJSResponse().exp
      },
      ...(this.#membershipConfirmation ? { membershipConfirmation: this.#membershipConfirmation } : {})
    };
  }

  //#region Pending membership confirmation (DEV-11022):

  #checkPendingMembershipCharge() {
    if (!this.#pendingMembershipService.shouldCheckPendingCharge()) return;

    this.#pendingMembershipService.getPendingCharge()
      .subscribe(pendingCharge => this.pendingMembershipCharge.set(pendingCharge));
  }

  /** Save button while a membership is waiting to be confirmed. */
  confirmMembershipBeforeSaving() {
    if (!this.#isFormValid()) return;
    this.#promptMembershipConfirmation(this.pendingMembershipCharge());
  }

  #promptMembershipConfirmation(pendingCharge: PendingMembershipCharge | null) {
    if (!pendingCharge) return;

    this.#pendingMembershipService.openConfirmation(pendingCharge).subscribe(confirmation => {
      // "Not now": the card is not sent and nothing is charged.
      if (!confirmation) return;

      this.#membershipConfirmation = confirmation;
      this.pendingMembershipCharge.set(null);
      this.#cdr.detectChanges();
      // An NMI token is spent on every attempt, so the save has to start from the
      // pay button again to tokenize the card still on screen.
      this.nmiPayButtonRef?.nativeElement?.click();
    });
  }

  //#endregion

  billingAddressToggleChangeHandler(checked: boolean) {
    if (checked && !this.states().length) {
      this.paymentMethodService.getStates().subscribe(states => this.states.set(states));
    }

    if (!checked) {
      this.billingForm?.reset();
    }
  }

  //#region NMI - CollectJS methods:

  #subscribeToCollectJsValidationChanges() {
    this.paymentMethodService.collectJSValidation$.pipe(
      tap((result: CollectJSValidationResult) => {
        switch (result.type) {
          case CollectJSFieldTypes.ccnumber:
            this.collectJS_ccnumber_validation.set(result);
            break;
          case CollectJSFieldTypes.ccexp:
            this.collectJS_ccexp_validation.set(result);
            break;
          case CollectJSFieldTypes.cvv:
            this.collectJS_cvv_validation.set(result);
            break;

          default:
            break;
        }

        if (!result.valid) {
          this.isDisabledSaveChanges.set(false);
          this.loadingService.close();
        }

        this.#cdr.detectChanges();
      })
    ).subscribe()
  }

  #subscribeToCollectJSResponse() {
    this.paymentMethodService.collectJSResponse$.pipe(
      tap((response: any) => {
        if (!response) return;
        this.collectJSResponse.set(response);
        this.#cdr.detectChanges();
        setTimeout(() => {
          if (this.isEditionModeEnable())
            this.handleSaveChangesClicked();
        }, 1000);
      })
    ).subscribe()
  }

  #subscribeToCollectJSTimeout() {
    this.paymentMethodService.collectJSTimeout$.pipe(
      tap(() => {
        this.loadingService.close();
        this.isDisabledSaveChanges.set(false);
      })
    )
  }

  //#endregion

  saveChangesHandler() {
    this.loadingService.isShowGlobalLoading.set(true);
    this.isDisabledSaveChanges.set(true);
  }
}
